import {
  ACTION,
  CopyCsv,
  CopyJson,
  Divider,
  IconName,
  LedgerAccountTypeEnum,
  MixpanelEventSource,
  PositionUpdateSourceEnum,
  ShowJson,
  filterByCellValueMenuItem,
  getRowNodesToOperateOn,
  getShowJSONContextItem,
  selectAll,
  useCallbackRef,
  useDisclosure,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
  useSyncedRef,
  type ColumnDef,
  type Exposure,
  type FilterableProperty,
  type Position,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, ICellRendererParams } from 'ag-grid-community';
import { compact, isNil } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { PositionTypeEnum, useFeatureFlag, useRoleAuth } from '../../../hooks';
import {
  pinClosePositionColumnColDefParams,
  useTradePositionColumn,
} from '../../../utils/columns/useTradePositionColumn';
import { BulkCloseDialog } from './BulkClosePosition/BulkCloseDialog';

import { useGetOpenTransactionsMenuItems } from '../../Trading/Markets/AccountLedgerEvents/useGetOpenTransactionsMenuItems';
import { useLegacyUpdateBalancesDialog } from '../BalancesV2/useLegacyUpdateBalancesDialog';
import { useUpdateBalancesDialog } from '../BalancesV2/useUpdateBalancesDialog';
import { useGetBulkClosablePositions, type ClosablePosition } from './BulkClosePosition/useGetBulkClosablePositions';
import { UpdatePositionsDialog } from './UpdatePositionsDialog';
import { colIDToFilterBuilderKey, type PositionsTabType } from './types';

export function PositionMenu(
  params: ICellRendererParams & {
    onShowJson(data: Exposure): void;
  }
) {
  return (
    <>
      <ShowJson {...params} />
      <Divider orientation="horizontal" />
      <CopyJson {...params} />
      <CopyCsv {...params} />
    </>
  );
}

interface UsePositionsMenuParams {
  openClause: UseFilterBuilderOutput['addAndOpenClause'] | undefined;
  filterableProperties: FilterableProperty<string>[];
  enableBulkClose?: boolean;
  tabType: PositionsTabType;

  /**
   * An optional callback you can implement in order to take control of the resolving of a right-click in a column
   * to the colId that should be used for filtering on the right-clicked column.
   *
   * The provided function returning undefined is effectively the same as the function itself not being provided -- column filtering
   * fallbacks to its standard code path.
   *
   * This is needed for supporting Tree Blotters.
   */
  getColIdForFiltering?: (params: GetContextMenuItemsParams) => string | undefined;
  /**
   * Whether or not to show the Open transactions action button in the context menu. Defaults to false.
   */
  showOpenTransactionsMenuItem?: boolean;
  /**
   * Whether or not to show the Close position column. Defaults to true if user is authorized to trade.
   */
  showClosePositionColumn?: boolean;
}

export function usePositionsMenu<T extends Position>({
  openClause,
  filterableProperties,
  enableBulkClose = false,
  tabType,
  getColIdForFiltering,
  showOpenTransactionsMenuItem,
  showClosePositionColumn,
}: UsePositionsMenuParams) {
  const bulkClosePositionsDialog = useDisclosure();
  const mixpanel = useMixpanel();
  const { handleClickJson, jsonModal } = useJsonModal();

  const { isAuthorized } = useRoleAuth();
  const authorizedToTrade = isAuthorized(ACTION.SUBMIT_ORDER);
  const authorizedToEditPosition = isAuthorized(ACTION.EDIT_POSITION);

  const {
    enableBulkClosePosition: enableBulkClosePositionFeatureFlag,
    enableAccountLedgerEventsPage,
    enableLegacyUpdateBalances,
  } = useFeatureFlag();
  const showBulkClose = enableBulkClose && enableBulkClosePositionFeatureFlag;

  const { getBulkClosablePositions } = useGetBulkClosablePositions();
  // We make this function stable so we don't do too much work and risk changing the suggested SendOrderArgs returned mid-confirmation.
  const stableGetBulkClosablePositions = useSyncedRef(getBulkClosablePositions);

  // State representing the positions set to be closed as their respective SendOrderArgs action representations
  const [closablePositions, setClosablePositions] = useState<ClosablePosition[]>([]);

  const [editPositionPrimer, setEditPositionPrimer] = useState<Position>();
  const updatePositionsDialog = useDisclosure({ onClose: () => setEditPositionPrimer(undefined) });

  const { open: updatePositionsDialogOpen } = updatePositionsDialog;
  const openUpdatePositionsDialog = useCallback(
    (primer?: Position) => {
      setEditPositionPrimer(primer);
      updatePositionsDialogOpen();
    },
    [updatePositionsDialogOpen]
  );

  const {
    legacyUpdateBalancesDialogComponent,
    getLegacyUpdateBalanceContextMenuItem,
    openLegacyUpdateBalancesDialog,
    showLegacyUpdateBalancesButton,
  } = useLegacyUpdateBalancesDialog<T>({
    enabled: enableLegacyUpdateBalances,
    getMarketAccount: getPositionMarketAccount,
    getAsset: getPositionAsset,
    blotterMarketAccountField: 'MarketAccount' satisfies keyof Position,
  });

  const {
    updateBalancesDialogComponent: adjustMarketAccountPositionsDialogComponent,
    getUpdateBalanceContextMenuItem: getAdjustMarketAccountContextMenuItem,
    openUpdateBalancesDialog: openAdjustMarketAccountPositionsDialog,
    showUpdateBalancesButton: showAdjustMarketAccountPositionsButton,
  } = useUpdateBalancesDialog<T>({
    enabled: !enableLegacyUpdateBalances,
    getMarketAccount: getPositionMarketAccount,
    getAsset: getPositionAsset,
    blotterMarketAccountField: 'MarketAccount' satisfies keyof Position,
    requireBalancePublishForContextMenu: false,
  });

  const { open: bulkClosePositionsDialogOpen } = bulkClosePositionsDialog;
  const openBulkClosePositionDialog = useCallback(
    (closablePositions: ClosablePosition[]) => {
      setClosablePositions(closablePositions);
      bulkClosePositionsDialogOpen();
    },
    [bulkClosePositionsDialogOpen]
  );

  const getOpenTransactionsMenuItems = useGetOpenTransactionsMenuItems();
  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();
  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<T>) => {
    const newSelectedPositions = compact(getRowNodesToOperateOn(params).map(node => node.data));

    const data = params?.node?.data;
    const groupData = params?.node?.groupData;
    if (isNil(data) && isNil(groupData)) {
      return [];
    }

    const items = [
      ...(data ? [getShowJSONContextItem({ params, handleClickJson }), 'separator'] : []),
      selectAll(params, mixpanel),
      'separator',
      ...getDefaultContextMenuItems(params),
    ];

    if (openClause) {
      // if a manual resolver is passed, use it. if not, colID is undefined, which will cause filterByCellValueItem to do its default thing
      const colID = getColIdForFiltering?.(params);

      items.unshift(
        ...filterByCellValueMenuItem({
          params,
          filterableProperties,
          openClause,
          colIDToFilterBuilderKey,
          mixpanel,
          colID,
        })
      );
    }

    if (showBulkClose && authorizedToTrade && newSelectedPositions.length > 0) {
      const closeOrderArgs = stableGetBulkClosablePositions.current(newSelectedPositions);
      if (closeOrderArgs.length > 0) {
        items.unshift('separator');
        items.unshift({
          name: `Close ${closeOrderArgs.length} position(s)`,
          action: () => openBulkClosePositionDialog(closeOrderArgs),
          icon: `<i class="ag-icon ${IconName.Close}"/>`,
        });
      }
    }

    if (authorizedToEditPosition && data != null && data.PositionSource === PositionUpdateSourceEnum.Internal) {
      if (tabType === 'Sub Accounts') {
        items.push('separator');
        items.push({
          name: 'Adjust Sub Account Position',
          action: () => openUpdatePositionsDialog(data),
          icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
        });
      } else if (
        enableLegacyUpdateBalances &&
        (tabType === 'Derivatives' || tabType === 'Unified' || tabType === 'Spot')
      ) {
        items.push('separator');
        items.push({
          name: 'Edit position',
          action: () => openUpdatePositionsDialog(data),
          icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
        });
      }
    }

    const updateBalancesItem = enableLegacyUpdateBalances
      ? getLegacyUpdateBalanceContextMenuItem(params)
      : getAdjustMarketAccountContextMenuItem(params);
    if (updateBalancesItem && (tabType === 'Derivatives' || tabType === 'Unified' || tabType === 'Spot')) {
      items.push('separator');
      items.push(updateBalancesItem);
    }

    if (showOpenTransactionsMenuItem && enableAccountLedgerEventsPage && data != null) {
      const accountType =
        tabType === 'Sub Accounts' ? LedgerAccountTypeEnum.SubAccount : LedgerAccountTypeEnum.MarketAccount;
      const asset = data.Asset;
      const account = tabType === 'Sub Accounts' ? data.SubAccount : data.MarketAccount;
      // can add more to this ok statement in the future
      items.push('separator');
      items.push(
        ...getOpenTransactionsMenuItems({
          asset,
          account,
          accountType,
          source: MixpanelEventSource.PositionsBlotter,
          showAccountOption: true,
        })
      );
    }

    return compact(items);
  });

  const dialogs = useMemo(
    () => (
      <>
        <BulkCloseDialog closablePositions={closablePositions} dialog={bulkClosePositionsDialog} />
        {(tabType === 'Sub Accounts' || enableLegacyUpdateBalances) && (
          <UpdatePositionsDialog
            dialogProps={updatePositionsDialog}
            positionPrimer={editPositionPrimer}
            positionType={
              tabType === 'Sub Accounts' ? PositionTypeEnum.SubAccountPosition : PositionTypeEnum.MarketAccountPosition
            }
          />
        )}
        {legacyUpdateBalancesDialogComponent}
        {adjustMarketAccountPositionsDialogComponent}
        {jsonModal}
      </>
    ),
    [
      jsonModal,
      closablePositions,
      bulkClosePositionsDialog,
      updatePositionsDialog,
      editPositionPrimer,
      tabType,
      enableLegacyUpdateBalances,
      legacyUpdateBalancesDialogComponent,
      adjustMarketAccountPositionsDialogComponent,
    ]
  );

  // We want to constrain the security filtering to market account if we're in any other blotter than the sub account one,
  // because on those records we don't have any market accounts, so need to keep it loose.
  const constrainSecuritiesToMarket = tabType !== 'Sub Accounts';
  const closePositionColumn = useTradePositionColumn<Position>({
    assetField: 'Asset',
    action: 'Close',
    columnId: 'closePosition',
    getAmount: getPositionAmount,
    colDefParams: pinClosePositionColumnColDefParams,
    getAssetType: getPositionAssetType,
    marketAccountField: 'MarketAccount',
    subAccountField: 'SubAccount',
    constrainSecuritiesToMarket,
  });

  const shouldShowClosePositionColumn =
    showClosePositionColumn !== undefined ? showClosePositionColumn : authorizedToTrade;

  const columns = useMemo(
    () =>
      compact([
        shouldShowClosePositionColumn ? closePositionColumn : undefined,
        {
          type: 'hamburgerMenu',
          id: 'rowMenu',
          params: {
            renderItems: params => <PositionMenu {...params} onShowJson={handleClickJson} />,
          },
        },
      ]) satisfies ColumnDef<Position>[],
    [handleClickJson, closePositionColumn, shouldShowClosePositionColumn]
  );

  return {
    columns,
    getContextMenuItems,
    dialogs,
    onClickJson: handleClickJson,
    openUpdatePositionDialog: updatePositionsDialog.open,
    openUpdateBalancesDialog: enableLegacyUpdateBalances
      ? openLegacyUpdateBalancesDialog
      : openAdjustMarketAccountPositionsDialog,
    showUpdateBalancesButton: enableLegacyUpdateBalances
      ? showLegacyUpdateBalancesButton
      : showAdjustMarketAccountPositionsButton,
    openBulkClosePositionDialog,
  };
}

function getPositionAssetType(position: Position) {
  return position.AssetType;
}

function getPositionMarketAccount(position: Position) {
  return position.MarketAccount;
}

function getPositionAsset(position: Position) {
  return position.Asset;
}

function getPositionAmount(position: Position) {
  return position.Amount;
}
